import { useV2NewEntry } from "src/generatorV2/hooks/useV2NewEntry";
import "../App.scss";

import { useEffect, useMemo, useRef } from "react";
import { FormProvider, useForm, useFormContext } from "react-hook-form";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { GeneratorV2 } from "src/generatorV2";
import { useV2AutoSave } from "src/generatorV2/hooks/useV2AutoSave";
import { useV2EntryLock } from "src/generatorV2/hooks/useV2EntryLock";
import ApiErrorView from "src/components/shared/ApiErrorView/ApiErrorView";
import Loader from "src/components/shared/Loader/Loader";
import { useGetApi, useIsNewEntry } from "src/hooks";
import { isPageApiError } from "src/interfaces/ApiError";
import { useDispatch, useSelector } from "src/redux/hooks";
import { initEntryLockData, resetEntryLock, clearSilentEntryTransition } from "src/redux/slices/entrySlice";
import {
  clearPrevEntryData,
  reconcileCreatedEntryData,
  resetState,
  setIsInitialInsert,
  setLogbookData,
  setLogbookDataMinimalV2,
} from "src/redux/slices/generatorSlice";
import { store } from "src/redux/store";
import { isNewEntryRouteId, resolveEntryId } from "src/utils";
import { Flex, PageContainer } from "src/styled";

import { clearNewSearchParam } from "./utils/navigateToLogbookEntry";
import useResetGuernsey from "../components/shared/GuernseyMulti/useResetGuernsey";
import { useAutoSave } from "./hooks/useAutoSave";
import { useEntryLock } from "./hooks/useEntryLock";
import { useFetchSnapshot } from "./hooks/useFetchSnapshot";
import { useGetBridgeUsers } from "./hooks/useGetBridgeUsers";
import { useHighlightRequiredOnLoad } from "./hooks/useHighlightRequiredOnLoad";
import useNewEntryRequests from "./hooks/useNewEntryRequests";
import LogBar from "./LogBar/LogBar";
import MedSigningOverrideSelect from "./LogBar/MedSigningOverrideSelect";
import MedStatusSelect from "./LogBar/MedStatusSelect";
import Logbook from "./Logbook/Logbook";
import Modals from "./Modals";

const GeneratorV1Hooks = () => {
  useAutoSave();
  useEntryLock();
  useGetBridgeUsers();
  useNewEntryRequests();
  useHighlightRequiredOnLoad();
  return null;
};

const GeneratorV2Hooks = () => {
  useV2EntryLock();
  useGetBridgeUsers();
  useNewEntryRequests();
  useV2AutoSave({ enableAutosave: true });
  useV2NewEntry();
  return null;
};

interface GeneratorProps {
  silentTargetIdRef: React.MutableRefObject<string | null>;
}

const Generator = ({ silentTargetIdRef }: GeneratorProps) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { logbookId, idType, lbeidOrPartId } = useParams();
  const [, setSearchParams] = useSearchParams();
  const latestEntryId = useSelector(
    state => state.generatorSlice.latestEntry.id,
  );
  const isNewEntry = useIsNewEntry();
  const resetGuernsey = useResetGuernsey();
  const pageRef = useRef<HTMLDivElement | null>(null);
  const { data, refetch, error, isLoading } = useGetApi<any>({
    request: "get-logbook",
    body: {
      _part: String(idType) === "partid",
      params: {
        lbid: logbookId,
        [String(idType)]: lbeidOrPartId,
      },
    },
    // Restricted entries also raise the bottom-left toast alongside the 403
    // page, so the server's reason is surfaced before the redirect effect runs.
    notifyForbidden: true,
  });

  const loadedEntryId = useMemo(
    () => resolveEntryId(data?.data?.content?.data?.latest),
    [data],
  );

  // Detect the silent "/new" -> "/:realId" route swap that happens after the
  // first draft autosave. During that swap the URL param change triggers a
  // background get-logbook refetch; without this guard `isStaleData` would flip
  // true and flash a full-page Loader, making the just-entered data appear to
  // vanish until the refetch resolves. We track it with refs (derived during
  // render) so the Loader never shows for even a single frame.
  const prevLbeidRef = useRef(lbeidOrPartId);
  const justCreatedEntryRef = useRef(false);
  const reconcileCreatedEntryRef = useRef(false);
  const { reset: resetForm } = useFormContext();
  const backgroundRefetchIterator = useSelector(
    state => state.generatorSlice.backgroundRefetchIterator,
  );
  const backgroundRefreshRef = useRef(false);

  // Manual save refreshes in place: no remount, Loader or scroll jump.
  useEffect(() => {
    if (!backgroundRefetchIterator) return;
    backgroundRefreshRef.current = true;
    void refetch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [backgroundRefetchIterator]);

  if (prevLbeidRef.current !== lbeidOrPartId) {
    // New Entry keeps the previous entry's id in the URL (with ?new=true).
    const cameFromNewRoute =
      isNewEntryRouteId(prevLbeidRef.current) ||
      (!!lbeidOrPartId && lbeidOrPartId === silentTargetIdRef.current);
    const nowRealId = !!lbeidOrPartId && !isNewEntryRouteId(lbeidOrPartId);
    if (cameFromNewRoute && nowRealId) {
      justCreatedEntryRef.current = true;
      reconcileCreatedEntryRef.current = true;
    }
    prevLbeidRef.current = lbeidOrPartId;
  }

  // Clear the guard once the refetch for the new id has landed.
  if (
    justCreatedEntryRef.current &&
    loadedEntryId &&
    String(loadedEntryId) === String(lbeidOrPartId)
  ) {
    justCreatedEntryRef.current = false;
  }

  const isStaleData = useMemo(() => {
    if (!lbeidOrPartId || isNewEntryRouteId(lbeidOrPartId)) {
      return false;
    }
    // Keep the current (Redux/RHF-backed) content on screen during the silent
    // new-entry route swap instead of showing the Loader.
    if (justCreatedEntryRef.current) {
      return false;
    }
    if (!loadedEntryId) {
      return isLoading;
    }
    if (String(loadedEntryId) !== String(lbeidOrPartId)) {
      return true;
    }
    if (isNewEntry) {
      return false;
    }
    return String(latestEntryId) !== String(loadedEntryId);
  }, [isLoading, lbeidOrPartId, loadedEntryId, latestEntryId, isNewEntry]);

  useEffect(() => {
    if (lbeidOrPartId === "null") {
      navigate(`/logbook/${logbookId}/${idType}`, { replace: true });
    }
    if (!error || !logbookId || !idType || !lbeidOrPartId) {
      return;
    }
    if (isNewEntryRouteId(lbeidOrPartId)) {
      return;
    }
    if (loadedEntryId && String(loadedEntryId) === String(lbeidOrPartId)) {
      return;
    }
    if (latestEntryId && String(lbeidOrPartId) !== String(latestEntryId)) {
      navigate(`/logbook/${logbookId}/${idType}/${latestEntryId}`, {
        replace: true,
      });
      return;
    }
    if (!latestEntryId) {
      navigate(`/logbook/${logbookId}/${idType}`, { replace: true });
    }
  }, [
    error,
    idType,
    latestEntryId,
    lbeidOrPartId,
    loadedEntryId,
    logbookId,
    navigate,
  ]);

  // Determine if this is a V2 entry early (before data effect)
  const { isV2, lbEntry, entryInfo } = useMemo(() => {
    const latest = data?.data?.content?.data?.latest;
    if (!latest) {
      return { isV2: false, lbEntry: null, entryInfo: null };
    }
    const lbEntry = latest?.lbentry;
    const { lbentry, ...entryInfo } = latest;
    return { isV2: lbEntry?.generator === "v2", lbEntry, entryInfo };
  }, [data]);

  useEffect(() => {
    if (data) {
      // On the refetch after the silent new-entry route swap the user may still
      // be typing: apply only entry metadata and keep editing/activeAutosave.
      const isReconcileCreated = reconcileCreatedEntryRef.current;
      reconcileCreatedEntryRef.current = false;
      const isBackgroundRefresh = backgroundRefreshRef.current;
      backgroundRefreshRef.current = false;
      if (isReconcileCreated && !isV2 && !isBackgroundRefresh) {
        dispatch(reconcileCreatedEntryData(data));
      } else {
        // Fields re-register under the new cell UUIDs; drop the old ones.
        if (isBackgroundRefresh) resetForm();
        resetGuernsey();
        // For V2, set minimal Redux state to avoid unnecessary V1 processing
        if (isV2) {
          dispatch(setLogbookDataMinimalV2(data));
        } else {
          dispatch(setLogbookData(data));
        }
        dispatch(setIsInitialInsert(true));
      }
      const liveEntryState = store.getState().entrySlice;
      dispatch(
        initEntryLockData({
          ...data.data.content.data.lock,
          current: data?.data?.content?.data?.latest?.updated,
          editing: isReconcileCreated ? liveEntryState.editing : false,
          activeAutosave: isReconcileCreated
            ? liveEntryState.activeAutosave
            : false,
        }),
      );
    }
  }, [data, dispatch, resetGuernsey, resetForm, isV2]);

  useFetchSnapshot();

  useEffect(() => {
    clearNewSearchParam(setSearchParams);
    return () => {
      dispatch(resetState());
      dispatch(resetEntryLock());
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const memoizedRef = useMemo(() => pageRef, [pageRef]);

  if (isPageApiError(error)) {
    return <ApiErrorView error={error} />;
  }

  // Keep the header (LogBar + med selects) mounted while the entry is loading;
  // the Loader is scoped to the entry body only. The entry-scoped hooks stay
  // gated off during loading so they don't run without ready data.
  const isEntryLoading = (isLoading && !data) || isStaleData;

  return (
    <PageContainer id="page-container" ref={pageRef}>
      {!isEntryLoading &&
        (isV2 ? <GeneratorV2Hooks /> : <GeneratorV1Hooks />)}
      <LogBar />
      <Flex justify="end" gap={15}>
        <MedSigningOverrideSelect />
        <MedStatusSelect />
      </Flex>
      {isEntryLoading ? (
        <Loader />
      ) : isV2 ? (
        <GeneratorV2 logbookDef={lbEntry} entryInfo={entryInfo} />
      ) : (
        <Logbook
          pageRef={memoizedRef}
          htmlId="lbentry"
        />
      )}
      <Modals refetchLogbook={refetch || (() => {})} />
    </PageContainer>
  );
};

const GeneratorFormProvider = () => {
  const dispatch = useDispatch();
  const methods = useForm();
  const refetchIterator = useSelector(
    state => state.generatorSlice.refetchIterator,
  );
  const latestEntryId = useSelector(
    state => state.generatorSlice.latestEntry.id,
  );
  const silentEntryTransition = useSelector(
    state => state.entrySlice.silentEntryTransition,
  );
  const { lbeidOrPartId } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const isNewEntry = searchParams.get("new") === "true";
  // Navigation is async, so the silent-swap flag clears before the URL changes.
  const silentTargetIdRef = useRef<string | null>(null);
  if (silentEntryTransition && latestEntryId && latestEntryId !== "new") {
    silentTargetIdRef.current = String(latestEntryId);
  }
  const prevRouteRef = useRef({ lbeidOrPartId, isNewEntry });

  useEffect(() => {
    if (searchParams.get("new") !== "true") return;
    if (
      !lbeidOrPartId ||
      lbeidOrPartId === "new" ||
      lbeidOrPartId.includes("new=")
    ) {
      return;
    }
    if (
      !latestEntryId ||
      latestEntryId === "new" ||
      latestEntryId !== lbeidOrPartId
    ) {
      return;
    }
    clearNewSearchParam(setSearchParams);
  }, [lbeidOrPartId, latestEntryId, searchParams, setSearchParams]);

  useEffect(() => {
    if (silentEntryTransition) dispatch(clearSilentEntryTransition());
  }, [dispatch, silentEntryTransition]);

  // Reset form when entry changes or when switching to/from new entry
  useEffect(() => {
    const prev = prevRouteRef.current;
    prevRouteRef.current = { lbeidOrPartId, isNewEntry };
    if (
      prev.lbeidOrPartId === lbeidOrPartId &&
      prev.isNewEntry === isNewEntry
    ) {
      return;
    }
    const isSilentSwap =
      !!lbeidOrPartId && lbeidOrPartId === silentTargetIdRef.current;
    silentTargetIdRef.current = null;
    if (isSilentSwap) {
      return;
    }
    methods.reset();
    dispatch(clearPrevEntryData());
  }, [lbeidOrPartId, isNewEntry, methods, dispatch]);

  return (
    <FormProvider {...methods}>
      <Generator key={refetchIterator} silentTargetIdRef={silentTargetIdRef} />
    </FormProvider>
  );
};

export default GeneratorFormProvider;
